import {useEffect, useState, createRef, useRef} from "react";
import PropTypes from "prop-types";
import { injectIntl, defineMessages } from "react-intl";
import { Card, Col, Row, Form } from "react-bootstrap";
import { default as messageCenterMessages } from "../views/MessageCenter/MessageCenter.messages";
import { marked } from 'marked';
import * as Sentry from "@sentry/react";

// services
import { validate } from "../services/helpers";
import useFormatter from "../hooks/useFormatter";

// components
import TextInputWithAttachments from "./TextInputWithAttachments";

// hooks
import useAttachmentViewer from "../hooks/useAttachmentViewer";
import useMessages from "../hooks/useMessages";
import useMessageAttachments from "../hooks/useMessageAttachments";
import MessageContentComment from "./MessageContentComment";

// translations
export const messages = defineMessages({
    category: {
        id: "Kategoria",
        defaultMessage: 'Kategoria'
    },
    subject: {
        id: "Aihe",
        defaultMessage: 'Aihe'
    },
    commentSuccess: {
        id: "Kiitos viestistäsi!",
        defaultMessage: 'Kiitos viestistäsi!'
    },
    commentFailed: {
        id: "Viestin lähettämisessä tapahtui virhe. Yritä uudelleen.",
        defaultMessage: 'Viestin lähettämisessä tapahtui virhe. Yritä uudelleen.'
    },
    commentNotAvailable: {
        id: "Tämä viestiketju on ratkaistu ja suljettu.",
        defaultMessage: 'Tämä viestiketju on ratkaistu ja suljettu.'
    },
    messagePlaceholder: {
        id: "Kirjoita viesti",
        defaultMessage: 'Kirjoita viesti'
    },
    aiIsAnswering: {
        id: "Ropo AI generoi vastausta",
        defaultMessage: "Ropo AI generoi vastausta"
    },
    aiApprovalYes: {
        id: "Kyllä",
        defaultMessage: "Kyllä"
    },
    aiApprovalNo: {
        id: "Ei, tarvitsen lisää apua",
        defaultMessage: "Ei, tarvitsen lisää apua"
    },
    messageFlowContent: {
        id: "Viestiketju",
        defaultMessage: "Viestiketju"
    }
});

const POLLING_INTERVAL = 5000; //ms between ticket update requests

const MessageContent = (props) => {
    const { formatDate, formatTime } = useFormatter();
    const lng = props.intl.formatMessage;
    const [conversationHistory, setConversationHistory] = useState([]);
    const [inputValues, setInputValues] = useState({});
    const [newAttachments, setNewAttachments] = useState([]);
    const scrollMessagesRef = createRef();
    const [commentSubmitted, setCommentSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { openAttachmentFile } = useAttachmentViewer();
    const { getAttachmentData, createComment, fetchJobMessages, sendMessageAction } = useMessages();
    const { checkAttachments, fileConfig } = useMessageAttachments();
    const pollingRef = useRef(null);

    const id = props.message.id ?? '';
    const isSupport = props.message.is_support ?? false;
    const status = props.message.current_status?.status ?? '';
    const summary = props.message.summary ?? '';
    const description = props.message.description ?? '';
    const date = props.message.created_at ?? '';
    const reason = props.message.reason ?? '';
    const attachments = props.message.attachments ?? [];
    const comments= props.message.comments ?? [];
    const aiIsAnswering = props.message.ai_status === "responding";
    const aiNeedsApproval = props.message.ai_status === "waiting_for_user_approval";

    useEffect(() => {
        if (aiIsAnswering && pollingRef.current === null) {
            pollingRef.current = window.setInterval(async () => {
                await fetchJobMessages(props.message.jobid);
            }, POLLING_INTERVAL);
        } else if (!aiIsAnswering && pollingRef.current !== null) {
            window.clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        return () => {
            if (pollingRef.current !== null) {
                window.clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        }
    }, [aiIsAnswering]);

    useEffect(() => {
        // reset the reply composer's own state when switching to a different message thread,
        // otherwise attachments/text picked up in a previous thread could be sent along with this one
        setInputValues({});
        setNewAttachments([]);
    }, [id]);

    useEffect(() => {
        if (attachments.length > 0) {
            // if ticket has attachments, add them to comments and sort by date
            const combinedArray = checkAttachments(props.message, attachments, comments);
            setConversationHistory(combinedArray);
        } else if (comments.length > 0) {
            // ticket has no attachments, only use comments
            setConversationHistory(comments);
        } else if (conversationHistory.length > 0) {
            // ticket has no comments or attachments
            setConversationHistory([]);
        }
    }, [id, comments, attachments]);

    useEffect(() => {
        // scroll to the bottom of the message
        if (scrollMessagesRef.current) {
            scrollMessagesRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [conversationHistory]);

    /**
     * Open the file in a new tab.
     * @param data
     * @param {string} fileType
     */
    const openFileInNewTab = (data, fileType) => {
        try {
            openAttachmentFile(data, fileType);
        } catch (error) {
            Sentry.captureException(error);
            props.showAlerts({
                message: lng(messageCenterMessages.errorOccurredTryAgain),
                type: 'danger'
            });
        }
    }

    /**
     * Get attachment data and open it in a new tab.
     * @param {string} attachmentId
     * @param {string} messageId
     * @param {string} mimetype
     */
    const getAttachment = async (attachmentId, messageId, mimetype) => {
        try {
            const response = await getAttachmentData(attachmentId, messageId);
            openFileInNewTab(response, mimetype);
        } catch (error) {
            Sentry.captureException(error);
            // Pass the alert to MessageCenter.js
            props.showAlerts({
                message: lng(messageCenterMessages.errorOccurredTryAgain),
                type: 'danger'
            });
        }
    }

    /**
     * Handle changes in input and remove errors.
     *
     * @param {*} e
     */
    const handleInputs = (e) => {
        // remove validation errors
        if (e.target.checkValidity()) {
            e.target.classList.remove("is-invalid");
        } 

        setInputValues({ ...inputValues, [e.target.id]: e.target.value });
    }

    /**
     * Handle attachments and set them to state.
     * @param attachments
     */
    const handleAttachments = (attachments) => {
        setNewAttachments(attachments);
        props.setAttachments(attachments);
    }

    /**
     * Validate and map data for submit purposes.
     * 
     * @param {*} data 
     * @param {*} attachments
     */
    const validateAndMapDataForSubmit = (data, attachments) => {
        let requestData = {
            comment: data['message-input']
        };

        let objectIds = [];
        
        // Add object_ids for attachments
        for (const attachment of attachments) {
            if (typeof attachment.objectid !== 'undefined' && attachment.objectid !== '') {
                objectIds.push(attachment.objectid);
            }
        }
        requestData.object_ids = objectIds;

        // return request data that is ready for submit
        return requestData;
    }

    /**
     * Handle form submission to create a new comment.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (validate(e.currentTarget)) {
            setIsSubmitting(true);
            const formData = validateAndMapDataForSubmit(inputValues, newAttachments);
            try {
                await createComment(id, formData);
                    
                // Handle successful response
                props.showAlerts({
                    message: lng(messages.commentSuccess),
                    type: 'success'
                });

                setCommentSubmitted(true);
                setInputValues({});
                setNewAttachments([]);
                props.setAttachments([]);

                await fetchJobMessages(props.message.jobid);
                setIsSubmitting(false);
            } catch (error) {
                Sentry.captureException(error);
                // Handle error
                props.showAlerts({
                    message: lng(messages.commentFailed),
                    type: 'danger'
                });
            }
        }
    }

    // Function to update comment submit status value
    const updateCommentSubmitValue = (value) => {
        setCommentSubmitted(value);
    }

    const handleClickAttachment = (attachment) => {
        void getAttachment(attachment.id, id, attachment.mimetype);
    }

    const handleAiApproval = async (value, comment) => {
        try {
            setIsSubmitting(true);
            await sendMessageAction(id, 'user_approves_ai_comment', value, comment);
            await fetchJobMessages(props.message.jobid);
        } catch (error) {
            Sentry.captureException(error);
            props.showAlerts({
                message: lng(messageCenterMessages.errorOccurredTryAgain),
                type: 'danger'
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    const aiAnswerElement = <div className='ai-answering' aria-live="polite">
        <div className='dot-typing' />
        <div className='text-muted ml-12'>{lng(messages.aiIsAnswering)}</div>
    </div>

    const aiApprovalElement = <div className="d-grid d-lg-flex gap-2 ai-approval">
        <button
            className="btn btn-primary flex-grow-1"
            type="button"
            aria-label={lng(messages.aiApprovalYes)}
            onClick={() => handleAiApproval('yes', lng(messages.aiApprovalYes))}
        >
            {lng(messages.aiApprovalYes)}
        </button>
        <button
            className="btn btn-secondary flex-grow-1"
            type="button"
            aria-label={lng(messages.aiApprovalNo)}
            onClick={() => handleAiApproval('no', lng(messages.aiApprovalNo))}
        >
            {lng(messages.aiApprovalNo)}
        </button>
    </div>

    if (id === '') {
        return null;
    }
    
    return (
        <main id="main" aria-label={lng(messages.messageFlowContent)}>
            <Form className="message-center-form" id={props.formId} onSubmit={handleSubmit} noValidate>
                <Card key={'messagecontent-' + id} className='p-2 ps-sm-4 pe-sm-4 pb-0 messagecontent'>
                    <Card.Body>
                        <Row className={'message ' + (isSupport === true ? ' support' : 'customer')}>
                            <Col xs={12}>
                                <p className='mb-1'>{lng(messages.category)}: {reason ?? ''}</p>
                                <p className='mb-1'>{lng(messages.subject)}: {summary ?? ''}</p>
                                {description && description.length > 0 && (
                                    <p dangerouslySetInnerHTML={{ __html: marked(description ?? '') }}></p>
                                )}
                                <p className='text-end text-muted fs-6 mb-0'>
                                    {date !== '' ? formatDate(date) : ''} {date !== '' ? formatTime(date) : ''}
                                </p>
                            </Col>
                        </Row>
                        {conversationHistory.map((comment, index) => (
                            <MessageContentComment
                                key={comment.id}
                                attachments={attachments}
                                comment={comment}
                                sameDate={index > 0 && formatDate(comment.sent_date) === formatDate(conversationHistory[index - 1].sent_date)}
                                onClickAttachment={handleClickAttachment}
                            />
                        ))}
                        {aiIsAnswering && aiAnswerElement}
                        {aiNeedsApproval && aiApprovalElement}
                    </Card.Body>
                    {status !== 'Closed' ? (
                        <div className='fixed-element-bottom'>
                            <TextInputWithAttachments
                                disabled={aiIsAnswering || aiNeedsApproval}
                                fieldId="message-input"
                                fieldName={lng(messages.messagePlaceholder)}
                                required={true}
                                attachmentsEnabled={true}
                                acceptedFileAmount={fileConfig.acceptedFileAmount}
                                acceptedFileTypes={fileConfig.acceptedFileTypes}
                                acceptedFileSize={fileConfig.acceptedFileSize}
                                byteConversionFactor={fileConfig.byteConversionFactor}
                                sizeUnit={fileConfig.sizeUnit}
                                handleInputChange={handleInputs}
                                handleAttachments={handleAttachments}
                                commentSubmitted={commentSubmitted}
                                updateCommentSubmitValue={updateCommentSubmitValue}
                                formId={props.formId}
                                messageContent={true}
                                files={props.files}
                                isSubmitting={isSubmitting}
                            />
                        </div>
                    ) : (
                        <div className='commenting-not-available'>{lng(messages.commentNotAvailable)}</div>
                    )}
                    <div className="scroll-message-ref" ref={scrollMessagesRef}></div>
                </Card>
            </Form>
        </main>
    );
};

MessageContent.propTypes = {
    'intl': PropTypes.object,
    'message': PropTypes.object,
    'showAlerts': PropTypes.func,
    'formId': PropTypes.string,
    'files': PropTypes.array,
    'setAttachments': PropTypes.func
};

export default injectIntl(MessageContent);
